import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, AlertTriangle, Check, CalendarDays } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { piApi } from '../../services/api';
import { usePIStore } from '../../store/usePIStore';
import { useProjectStore } from '../../store/useProjectStore';
import { useOrgHierarchyStore } from '../../store/useOrgHierarchyStore';
import type {
  CadencePreview, ProgramIncrement, ReleaseOffset, Weekday,
} from '../../types';

interface Props {
  onClose: () => void;
  pi?: ProgramIncrement | null;
}

const WEEKDAYS: { id: Weekday; label: string }[] = [
  { id: 'monday', label: 'Mon' },
  { id: 'tuesday', label: 'Tue' },
  { id: 'wednesday', label: 'Wed' },
  { id: 'thursday', label: 'Thu' },
  { id: 'friday', label: 'Fri' },
  { id: 'saturday', label: 'Sat' },
  { id: 'sunday', label: 'Sun' },
];

const RELEASE_OFFSETS: { id: ReleaseOffset; label: string; hint: string }[] = [
  {
    id: 'onOrAfterSprintEnd',
    label: 'On or after sprint end',
    hint: 'First release weekday on or after the sprint ends — same day if it matches.',
  },
  {
    id: 'sameDayAsSprintEnd',
    label: 'Sprint end date',
    hint: 'Always the last day of the sprint, whatever weekday that is.',
  },
  {
    id: 'nextWeekAfterSprintEnd',
    label: 'Following week',
    hint: 'First release weekday strictly after the sprint — adds a settle week.',
  },
];

type Shape = 'daily' | 'weekly' | 'nweekly';

function shapeOf(days: number): Shape {
  if (days === 1) return 'daily';
  if (days === 7) return 'weekly';
  return 'nweekly';
}

export default function CreatePIModal({ onClose, pi }: Props) {
  const isEdit = !!pi;
  const { createPI, updatePI, fetchPIs } = usePIStore();
  const { projects } = useProjectStore();
  const {
    programs, releaseTrains,
    fetchPrograms, fetchReleaseTrains,
  } = useOrgHierarchyStore();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<CadencePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: pi?.name || '',
    description: pi?.description || '',
    programId: pi?.programId || '',
    releaseTrainId: pi?.releaseTrainId || '',
    startDate: pi?.startDate || '',
    sprintCount: pi?.sprintCount ?? 5,
    sprintLengthDays: pi?.sprintLengthDays ?? (pi?.sprintLengthWeeks ?? 2) * 7,
    sprintStartWeekday: (pi?.cadence?.sprintStartWeekday || '') as Weekday,
    releaseWeekday: (pi?.cadence?.releaseWeekday || '') as Weekday,
    releaseOffset: (pi?.cadence?.releaseOffset || 'onOrAfterSprintEnd') as ReleaseOffset,
    // BG-297: the sprint boundary. Daily sprints only switch over on their own when the
    // PI has a timezone, a boundary time and auto-advance -- none of which the form could
    // set, so they had to be patched in from the backend.
    timezone: pi?.cadence?.timezone
      || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    autoAdvance: Boolean(pi?.cadence?.autoAdvance),
    sprintBoundaryTime: pi?.cadence?.sprintBoundaryTime || '08:00',
    projectIds: pi?.projectIds || ([] as string[]),
  });

  const [weeks, setWeeks] = useState(
    Math.max(1, Math.round((form.sprintLengthDays || 14) / 7)),
  );

  useEffect(() => {
    fetchPrograms();
    fetchReleaseTrains();
  }, [fetchPrograms, fetchReleaseTrains]);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Release trains available for the chosen program
  const availableTrains = useMemo(
    () =>
      form.programId
        ? releaseTrains.filter((rt) => rt.programId === form.programId)
        : releaseTrains,
    [releaseTrains, form.programId],
  );

  // Projects offered are constrained to the chosen train, matching the
  // backend's validation so the picker can't produce a rejected payload.
  const availableProjects = useMemo(() => {
    if (!form.releaseTrainId) return projects;
    return projects.filter(
      (p) => !p.releaseTrainId || p.releaseTrainId === form.releaseTrainId,
    );
  }, [projects, form.releaseTrainId]);

  /** Pull cadence defaults from the selected train (new PIs only — an existing
   *  PI owns its cadence and must not be silently re-pointed). */
  const applyTrainDefaults = useCallback(async (trainId: string) => {
    if (!trainId || isEdit) return;
    try {
      const defaults = await piApi.cadenceDefaults(trainId);
      setForm((f) => ({
        ...f,
        sprintLengthDays: defaults.sprintLengthDays ?? f.sprintLengthDays,
        sprintStartWeekday: defaults.sprintStartWeekday ?? f.sprintStartWeekday,
        releaseWeekday: defaults.releaseWeekday ?? f.releaseWeekday,
        releaseOffset: defaults.releaseOffset ?? f.releaseOffset,
      }));
      if (defaults.sprintLengthDays) {
        setWeeks(Math.max(1, Math.round(defaults.sprintLengthDays / 7)));
      }
    } catch {
      /* defaults are a convenience — failing to load them isn't fatal */
    }
  }, [isEdit]);

  const cadencePayload = useCallback(() => ({
    name: form.name,
    startDate: form.startDate,
    sprintCount: form.sprintCount,
    sprintLengthDays: form.sprintLengthDays,
    cadence: {
      sprintStartWeekday: form.sprintStartWeekday,
      releaseWeekday: form.releaseWeekday,
      releaseOffset: form.releaseOffset,
      timezone: form.timezone,
      autoAdvance: form.autoAdvance,
      // The server refuses auto-advance without a boundary; without auto-advance the
      // boundary is not used, so it is not sent.
      sprintBoundaryTime: form.autoAdvance ? form.sprintBoundaryTime : '',
    },
  }), [form]);

  // Live calendar preview whenever the cadence changes on step 2
  useEffect(() => {
    if (step !== 2 || !form.startDate || !form.sprintCount) {
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await piApi.cadencePreview(cadencePayload());
        if (!cancelled) {
          setPreview(result);
          setPreviewError(null);
        }
      } catch (e: any) {
        if (!cancelled) {
          setPreview(null);
          setPreviewError(e?.message || 'Could not preview this cadence.');
        }
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [step, form.startDate, form.sprintCount, form.sprintLengthDays,
      form.sprintStartWeekday, form.releaseWeekday, form.releaseOffset,
      form.timezone, form.autoAdvance, form.sprintBoundaryTime,
      cadencePayload]);

  const shape = shapeOf(form.sprintLengthDays);

  const setShape = (next: Shape) => {
    if (next === 'daily') set('sprintLengthDays', 1);
    else if (next === 'weekly') set('sprintLengthDays', 7);
    else set('sprintLengthDays', Math.max(1, weeks) * 7);
  };

  const canAdvance =
    step === 1 ? form.name.trim() !== '' :
    step === 2 ? Boolean(form.startDate) && form.sprintCount >= 1 && !previewError :
    true;

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...cadencePayload(),
        description: form.description,
        programId: form.programId,
        releaseTrainId: form.releaseTrainId,
        projectIds: form.projectIds,
      };
      if (isEdit && pi) {
        await updatePI(pi._id, payload);
        toast.success('Program Increment updated');
      } else {
        await createPI(payload);
        toast.success(
          `Program Increment created with ${form.sprintCount} sprint${form.sprintCount === 1 ? '' : 's'}`,
        );
      }
      await fetchPIs();
      onClose();
    } catch (e: any) {
      // A 409 means retiming would discard sprint contents — surface it rather
      // than forcing, since force unassigns work items.
      const message = e?.message || 'Failed to save Program Increment';
      toast.error(message, { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  const toggleProject = (id: string) =>
    setForm((f) => ({
      ...f,
      projectIds: f.projectIds.includes(id)
        ? f.projectIds.filter((p) => p !== id)
        : [...f.projectIds, id],
    }));

  return (
    <Transition appear show as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {isEdit ? 'Edit Program Increment' : 'New Program Increment'}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Steps */}
                <div className="flex items-center gap-2 mb-5">
                  {['Basics', 'Cadence & calendar', 'Projects'].map((label, i) => {
                    const n = i + 1;
                    return (
                      <button
                        key={label}
                        onClick={() => n < step && setStep(n)}
                        className={clsx(
                          'flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full transition-colors',
                          n === step
                            ? 'bg-accent text-white'
                            : n < step
                            ? 'bg-accent/10 text-accent'
                            : 'text-gray-400 dark:text-gray-500',
                        )}
                      >
                        {n < step ? <Check size={12} /> : <span>{n}</span>}
                        {label}
                      </button>
                    );
                  })}
                </div>

                {/* ---- Step 1: basics ---- */}
                {step === 1 && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Name
                      </label>
                      <input
                        autoFocus
                        value={form.name}
                        onChange={(e) => set('name', e.target.value)}
                        placeholder="PI 2026.3"
                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Description
                      </label>
                      <textarea
                        rows={2}
                        value={form.description}
                        onChange={(e) => set('description', e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          Program
                        </label>
                        <select
                          value={form.programId}
                          onChange={(e) => {
                            set('programId', e.target.value);
                            set('releaseTrainId', '');
                          }}
                          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                        >
                          <option value="">— none —</option>
                          {programs.map((p) => (
                            <option key={p._id} value={p._id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          Release train
                        </label>
                        <select
                          value={form.releaseTrainId}
                          onChange={(e) => {
                            set('releaseTrainId', e.target.value);
                            applyTrainDefaults(e.target.value);
                          }}
                          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                        >
                          <option value="">— none —</option>
                          {availableTrains.map((rt) => (
                            <option key={rt._id} value={rt._id}>{rt.name}</option>
                          ))}
                        </select>
                        {!isEdit && (
                          <p className="mt-1 text-[11px] text-gray-400">
                            Its cadence pre-fills the next step; this PI then owns its own copy.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ---- Step 2: cadence ---- */}
                {step === 2 && (
                  <div className="grid grid-cols-2 gap-5">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          Sprint length
                        </label>
                        <div className="flex gap-1">
                          {([
                            ['daily', '1 day'],
                            ['weekly', '1 week'],
                            ['nweekly', 'Weeks'],
                          ] as [Shape, string][]).map(([id, label]) => (
                            <button
                              key={id}
                              onClick={() => setShape(id)}
                              className={clsx(
                                'flex-1 px-2 py-1.5 text-xs rounded-md border transition-colors',
                                shape === id
                                  ? 'bg-accent text-white border-accent'
                                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
                              )}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        {shape === 'nweekly' && (
                          <div className="mt-2 flex items-center gap-2">
                            <input
                              type="number"
                              min={1}
                              max={52}
                              value={weeks}
                              onChange={(e) => {
                                const w = Math.max(1, parseInt(e.target.value) || 1);
                                setWeeks(w);
                                set('sprintLengthDays', w * 7);
                              }}
                              className="w-20 px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                            />
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              weeks per sprint ({form.sprintLengthDays} days)
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Start date
                          </label>
                          <input
                            type="date"
                            value={form.startDate}
                            onChange={(e) => set('startDate', e.target.value)}
                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Sprints
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={60}
                            value={form.sprintCount}
                            onChange={(e) =>
                              set('sprintCount', Math.max(1, parseInt(e.target.value) || 1))
                            }
                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                          />
                        </div>
                      </div>

                      {shape !== 'daily' && (
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Sprints start on
                          </label>
                          <div className="flex flex-wrap gap-1">
                            {WEEKDAYS.map((d) => (
                              <button
                                key={d.id}
                                onClick={() =>
                                  set('sprintStartWeekday',
                                      form.sprintStartWeekday === d.id ? '' : d.id)
                                }
                                className={clsx(
                                  'px-2 py-1 text-[11px] rounded border transition-colors',
                                  form.sprintStartWeekday === d.id
                                    ? 'bg-accent text-white border-accent'
                                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300',
                                )}
                              >
                                {d.label}
                              </button>
                            ))}
                          </div>
                          <p className="mt-1 text-[11px] text-gray-400">
                            Unset uses the start date as given.
                          </p>
                        </div>
                      )}

                      {shape === 'daily' && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                          Daily sprints run consecutive working days and skip
                          weekends and blackout dates. Weekday anchoring doesn't apply.
                        </p>
                      )}

                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          Releases push on
                        </label>
                        <div className="flex flex-wrap gap-1">
                          {WEEKDAYS.map((d) => (
                            <button
                              key={d.id}
                              onClick={() =>
                                set('releaseWeekday',
                                    form.releaseWeekday === d.id ? '' : d.id)
                              }
                              className={clsx(
                                'px-2 py-1 text-[11px] rounded border transition-colors',
                                form.releaseWeekday === d.id
                                  ? 'bg-accent text-white border-accent'
                                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300',
                              )}
                            >
                              {d.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {form.releaseWeekday && (
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Release timing
                          </label>
                          <select
                            value={form.releaseOffset}
                            onChange={(e) =>
                              set('releaseOffset', e.target.value as ReleaseOffset)
                            }
                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                          >
                            {RELEASE_OFFSETS.map((o) => (
                              <option key={o.id} value={o.id}>{o.label}</option>
                            ))}
                          </select>
                          <p className="mt-1 text-[11px] text-gray-400">
                            {RELEASE_OFFSETS.find((o) => o.id === form.releaseOffset)?.hint}
                          </p>
                        </div>
                      )}

                      <div>
                        <label htmlFor="pi-timezone" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          Timezone
                        </label>
                        <input
                          id="pi-timezone"
                          list="pi-timezones"
                          value={form.timezone}
                          onChange={(e) => set('timezone', e.target.value.trim())}
                          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                        />
                        <datalist id="pi-timezones">
                          {['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
                            'Europe/London', 'Europe/Berlin', 'Asia/Kolkata', 'Asia/Singapore',
                            'Australia/Sydney', 'UTC'].map((z) => <option key={z} value={z} />)}
                        </datalist>
                      </div>

                      <div>
                        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                          <input
                            id="pi-auto-advance"
                            type="checkbox"
                            checked={form.autoAdvance}
                            onChange={(e) => set('autoAdvance', e.target.checked)}
                          />
                          Switch sprints automatically
                        </label>
                        {form.autoAdvance && (
                          <div className="mt-2 flex items-center gap-2">
                            <label htmlFor="pi-boundary" className="text-xs text-gray-500 dark:text-gray-400">
                              Each sprint starts at
                            </label>
                            <input
                              id="pi-boundary"
                              type="time"
                              value={form.sprintBoundaryTime}
                              onChange={(e) => set('sprintBoundaryTime', e.target.value)}
                              className="px-2 py-1 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                            />
                            <span className="text-[11px] text-gray-400">{form.timezone}</span>
                          </div>
                        )}
                        <p className="mt-1 text-[11px] text-gray-400">
                          {form.autoAdvance
                            ? "At this time on each sprint's start date the previous sprint closes and its unfinished items roll forward."
                            : 'Sprints are started by hand (Start Sprint).'}
                        </p>
                      </div>
                    </div>

                    {/* Calendar preview */}
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <CalendarDays size={14} className="text-accent" />
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                          Generated calendar
                        </span>
                      </div>

                      {previewError && (
                        <p className="text-xs text-red-600 dark:text-red-400">
                          {previewError}
                        </p>
                      )}

                      {!previewError && !form.startDate && (
                        <p className="text-xs text-gray-400">
                          Pick a start date to preview the calendar.
                        </p>
                      )}

                      {preview && (
                        <>
                          {preview.warnings.length > 0 && (
                            <div className="mb-2 space-y-1">
                              {preview.warnings.map((w, i) => (
                                <div
                                  key={i}
                                  className="flex items-start gap-1.5 px-2 py-1.5 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-[11px]"
                                >
                                  <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                                  <span>{w}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                            <table className="w-full text-[11px]">
                              <thead className="bg-gray-50 dark:bg-gray-750 text-gray-500 dark:text-gray-400">
                                <tr>
                                  <th className="text-left px-2 py-1.5 font-medium">#</th>
                                  <th className="text-left px-2 py-1.5 font-medium">Start</th>
                                  <th className="text-left px-2 py-1.5 font-medium">End</th>
                                  <th className="text-left px-2 py-1.5 font-medium">Release</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {preview.sprints.map((s) => (
                                  <tr key={s.sprintNumber} className="text-gray-700 dark:text-gray-300">
                                    <td className="px-2 py-1.5">{s.sprintNumber}</td>
                                    <td className="px-2 py-1.5 font-mono">{s.startDate}</td>
                                    <td className="px-2 py-1.5 font-mono">{s.endDate}</td>
                                    <td className="px-2 py-1.5 font-mono">
                                      {s.releaseDate}
                                      {s.releaseShifted && (
                                        <span className="ml-1 text-amber-600 dark:text-amber-400">
                                          shifted
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                            {preview.startDate} → {preview.endDate}
                            {preview.sprintLengthWeeks > 0 &&
                              ` · ${preview.sprintLengthWeeks}-week sprints`}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* ---- Step 3: projects ---- */}
                {step === 3 && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      Each selected project gets one release per sprint once release
                      generation is enabled.
                      {form.releaseTrainId && ' Limited to projects on the chosen release train.'}
                    </p>
                    {availableProjects.length === 0 ? (
                      <p className="text-sm text-gray-400 py-6 text-center">
                        No eligible projects.
                      </p>
                    ) : (
                      <div className="max-h-80 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                        {availableProjects.map((p) => {
                          const checked = form.projectIds.includes(p._id);
                          return (
                            <label
                              key={p._id}
                              className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleProject(p._id)}
                                className="rounded border-gray-300 text-accent focus:ring-accent"
                              />
                              <span className="flex-1 min-w-0 text-sm text-gray-800 dark:text-gray-200 truncate">
                                {p.displayId && (
                                  <span className="text-[10px] font-mono text-gray-400 mr-1">
                                    {p.displayId}
                                  </span>
                                )}
                                {p.name}
                              </span>
                              {!p.releaseTrainId && (
                                <span className="text-[10px] text-gray-400">no train</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    )}
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      {form.projectIds.length} selected
                      {preview &&
                        form.projectIds.length > 0 &&
                        ` · ${form.projectIds.length * preview.sprints.length} releases would be generated`}
                    </p>
                  </div>
                )}

                {/* Footer */}
                <div className="flex justify-between items-center pt-5 mt-1">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Cancel
                  </button>
                  <div className="flex gap-2">
                    {step > 1 && (
                      <button
                        onClick={() => setStep(step - 1)}
                        className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        Back
                      </button>
                    )}
                    {step < 3 ? (
                      <button
                        onClick={() => setStep(step + 1)}
                        disabled={!canAdvance}
                        className="px-4 py-2 text-sm bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50"
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        onClick={submit}
                        disabled={loading}
                        className="px-4 py-2 text-sm bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50"
                      >
                        {loading
                          ? 'Saving…'
                          : isEdit
                          ? 'Save changes'
                          : 'Create Program Increment'}
                      </button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
